import React, { useCallback, useEffect, useRef, useState } from "react";
import { Badge, Button, ButtonGroup, Form, Modal, Spinner, Tab, Tabs } from "react-bootstrap";
import {
  captureChartEvaluation,
  evaluateChart,
  getChartEvaluationHistory,
  getChartEvaluationJob,
  getChartEvaluationModels,
  getChartEvaluationProfile,
  getChartEvaluationProviders,
  previewChartEvaluation,
  processCommand,
  saveChartEvaluationProfile,
} from "../../utils/utils";
import TelegramIdLogin from "../TelegramIdLogin";
import CodexEvaluationReport from "../CodexEvaluationReport";
import { captureBrowserCharts } from "../../chartEvaluation/browserChartCapture";
import { selectExecutableEvaluationPlan } from "../../chartEvaluation/chartEvaluationTrade";
import { useUserSession } from "../../auth/UserSessionContext";

const SIDE_OPTIONS = ["LONG", "SHORT", "BOTH"];

const recordToResult = (record) => record?.result ? {
  evaluation: { ...record.result, evaluatedAt: record.result.evaluatedAt || record.completedAt },
  model: record.model,
  usage: record.usage,
  symbol: record.symbol,
  requestedSide: record.requestedSide,
  createdAt: record.completedAt || record.createdAt,
  record,
} : null;

function ChartEvaluationPopup({ show, onHide, symbol, eventPrice, originatingEvent, onDrawLevels, onTakeTrade }) {
  const { login } = useUserSession();
  const eventKey = String(originatingEvent?.codexTargetId || originatingEvent?.id || originatingEvent?.time || "");
  const scopeRef = useRef({ symbol, show, eventKey });
  if (scopeRef.current.symbol !== symbol || scopeRef.current.show !== show || scopeRef.current.eventKey !== eventKey) {
    scopeRef.current = { symbol, show, eventKey };
  }
  const captureController = useRef(null);
  const settingsRequest = useRef(0);
  const saveRequest = useRef(0);
  const operationVersion = useRef(0);
  const [models, setModels] = useState([]);
  const [providers, setProviders] = useState([]);
  const [provider, setProvider] = useState("CUSTOM_AI");
  const [history, setHistory] = useState({ CUSTOM_AI: [], CODEX: [] });
  const [profile, setProfile] = useState({ defaultModel: "gpt-5-nano", inputSelection: { screenshots: true, structuredData: true } });
  const [requestedSide, setRequestedSide] = useState("BOTH");
  const [model, setModel] = useState("");
  const [codexModel, setCodexModel] = useState("");
  const [openAiKey, setOpenAiKey] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [requestPreview, setRequestPreview] = useState(null);
  const [previewInput, setPreviewInput] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [authenticationRequired, setAuthenticationRequired] = useState(false);
  const [activeJob, setActiveJob] = useState(null);
  const [quickValidating, setQuickValidating] = useState(false);
  const [quickValidationRequested, setQuickValidationRequested] = useState(false);
  const [previewExpired, setPreviewExpired] = useState(false);

  const loadSettings = useCallback(async () => {
    const scope = scopeRef.current;
    const request = ++settingsRequest.current;
    try {
      const [modelResponse, profileResponse, providerResponse, historyResponse] = await Promise.all([
        getChartEvaluationModels(),
        getChartEvaluationProfile(),
        getChartEvaluationProviders(),
        getChartEvaluationHistory(symbol),
      ]);
      if (scopeRef.current !== scope || request !== settingsRequest.current) return;
      const nextProfile = profileResponse.profile || profileResponse;
      setModels(modelResponse.models || []);
      setProviders(providerResponse.providers || []);
      const nextCodexProvider = (providerResponse.providers || []).find((item) => item.id === "CODEX");
      setCodexModel(nextCodexProvider?.manualModel || nextCodexProvider?.model || "");
      setHistory(historyResponse.providers || { CUSTOM_AI: [], CODEX: [] });
      setProfile(nextProfile || { defaultModel: "gpt-5-nano", inputSelection: { screenshots: true, structuredData: true } });
      setModel(nextProfile?.defaultModel || "gpt-5-nano");
      setShowSettings(!nextProfile?.keyConfigured);
      setAuthenticationRequired(false);
      setError("");
    } catch (cause) {
      if (scopeRef.current !== scope || request !== settingsRequest.current) return;
      if (cause.status === 401) {
        setAuthenticationRequired(true);
        setError("");
        return;
      }
      setError(cause.message || "Could not load chart evaluation settings.");
    }
  }, [symbol]);

  const completeLogin = useCallback(({ mode, fields }) => {
    login(fields, mode)
      .then(() => loadSettings())
      .catch(() => setError("Sign-in is temporarily unavailable. Please try again."));
  }, [login, loadSettings]);

  useEffect(() => {
    operationVersion.current += 1;
    captureController.current?.abort();
    setCapturing(false);
    setEvaluating(false);
    setPreviewing(false);
    setHistory({ CUSTOM_AI: [], CODEX: [] });
    if (!show) return;
    setError("");
    setResult(null);
    setPreviewInput(null);
    setRequestPreview(null);
    setShowPreview(false);
    setQuickValidating(false);
    setQuickValidationRequested(false);
    const storedJobId = localStorage.getItem(`chartEvaluationJob:${String(symbol || "").toUpperCase()}`);
    setActiveJob(storedJobId ? { id: storedJobId, state: "PENDING" } : null);
    loadSettings();
  // Refresh when the modal is opened for a different market.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, symbol, eventKey]);

  useEffect(() => () => {
    scopeRef.current = {};
    captureController.current?.abort();
  }, []);

  useEffect(() => {
    setPreviewExpired(false);
    if (!requestPreview?.expiresAt) return undefined;
    const remaining = Number(requestPreview.expiresAt) - Date.now();
    if (remaining <= 0) { setPreviewExpired(true); return undefined; }
    const timer = window.setTimeout(() => setPreviewExpired(true), remaining);
    return () => window.clearTimeout(timer);
  }, [requestPreview]);

  useEffect(() => {
    operationVersion.current += 1;
    setPreviewInput(null);
    setRequestPreview(null);
    setShowPreview(false);
  }, [provider, requestedSide, model, codexModel, profile.inputSelection?.screenshots, profile.inputSelection?.structuredData]);

  useEffect(() => {
    if (!show) return undefined;
    const handleAuthentication = () => loadSettings();
    window.addEventListener("chart-evaluation-authenticated", handleAuthentication);
    return () => window.removeEventListener("chart-evaluation-authenticated", handleAuthentication);
  }, [loadSettings, show]);

  useEffect(() => {
    setResult(recordToResult(history?.[provider]?.find((record) => record.symbol === symbol)));
  }, [history, provider, symbol]);

  useEffect(() => {
    if (!show || !activeJob?.id || !["PENDING", "LEASED"].includes(activeJob.state)) return undefined;
    let cancelled = false;
    let inFlight = false;
    const poll = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const job = await getChartEvaluationJob(activeJob.id);
        if (cancelled) return;
        if (job.symbol && job.symbol !== symbol) return;
        setActiveJob(job);
        if (job.state === "COMPLETED") {
          localStorage.removeItem(`chartEvaluationJob:${String(symbol || "").toUpperCase()}`);
          await loadSettings();
          if (!cancelled) setActiveJob(null);
          window.dispatchEvent(new Event("chart_evaluation_saved"));
        } else if (job.state === "FAILED") {
          localStorage.removeItem(`chartEvaluationJob:${String(symbol || "").toUpperCase()}`);
          setError(job.error || "Codex chart evaluation failed.");
        }
      } catch (cause) {
        if (!cancelled) setError(cause.message || "Could not read Codex evaluation status.");
      } finally {
        inFlight = false;
      }
    };
    poll();
    const timer = window.setInterval(poll, 2500);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [activeJob?.id, activeJob?.state, loadSettings, symbol, show]);

  const saveProfile = async () => {
    const scope = scopeRef.current;
    const request = ++saveRequest.current;
    try {
      const response = await saveChartEvaluationProfile({
        defaultModel: model,
        inputSelection: profile.inputSelection,
        openAiKey: openAiKey || undefined,
      });
      if (scopeRef.current !== scope || saveRequest.current !== request) return;
      setProfile(response.profile || response);
      setOpenAiKey("");
      setShowSettings(false);
    } catch (cause) {
      if (scopeRef.current !== scope || saveRequest.current !== request) return;
      setError(cause.message || "Could not save chart evaluation settings.");
    }
  };

  const captureEvaluationInput = async () => {
    const scope = scopeRef.current;
    captureController.current?.abort();
    const controller = new AbortController();
    captureController.current = controller;
    const capturedAt = Date.now();
    setCapturing(true);
    try {
      const screenshotsEnabled = profile.inputSelection?.screenshots !== false;
      const structuredDataEnabled = profile.inputSelection?.structuredData !== false;
      if (!screenshotsEnabled && !structuredDataEnabled) {
        throw new Error("Enable chart screenshots or OHLCV context.");
      }
      const [screenshots, capture] = await Promise.all([
        captureBrowserCharts({ symbol, enabled: screenshotsEnabled, signal: controller.signal }),
        structuredDataEnabled
          ? captureChartEvaluation({ symbol, screenshots: false, structuredData: true }, { signal: controller.signal })
          : Promise.resolve({ context: { symbol } }),
      ]);
      if (scopeRef.current !== scope || controller.signal.aborted) throw new Error("Chart capture cancelled.");
      return {
        provider,
        symbol,
        requestedSide,
        model: provider === "CODEX" ? codexModel : model,
        originatingEvent,
        screenshots,
        context: {
          symbol,
          capturedAt,
          snapshotAsOf: Math.floor(capturedAt / 1000),
          snapshotId: window.crypto.randomUUID(),
          ...(capture.context || {}),
          eventPrice: eventPrice ?? originatingEvent?.price ?? null,
          eventOccurredAt: originatingEvent?.occurredAt ?? originatingEvent?.time ?? null,
          screenshotBasis: "Live forming candles; structured candles are aligned to the last closed 15M candle.",
          evidenceLimitations: structuredDataEnabled ? [] : ["No structured OHLCV or verified current quote supplied. Use visible chart evidence only; abstain from exact execution levels if unreadable."],
        },
      };
    } finally {
      controller.abort();
      if (scopeRef.current === scope) setCapturing(false);
    }
  };

  const closePopup = () => {
    scopeRef.current = {};
    captureController.current?.abort();
    setPreviewInput(null);
    setRequestPreview(null);
    setShowPreview(false);
    onHide();
  };

  const previewRequest = async () => {
    const scope = scopeRef.current;
    const version = operationVersion.current;
    setPreviewing(true);
    setError("");
    try {
      const input = await captureEvaluationInput();
      if (scopeRef.current !== scope || operationVersion.current !== version) return;
      const preview = await previewChartEvaluation(input);
      if (scopeRef.current !== scope || operationVersion.current !== version) return;
      setPreviewInput(input);
      setRequestPreview(preview);
      setShowPreview(true);
    } catch (cause) {
      if (scopeRef.current !== scope) return;
      if (cause.status === 401) {
        setAuthenticationRequired(true);
        setError("");
        return;
      }
      setError(cause.message || "Could not preview the chart evaluation request.");
    } finally {
      if (scopeRef.current === scope) setPreviewing(false);
    }
  };

  const evaluate = async (frozenInput = null) => {
    const scope = scopeRef.current;
    const version = operationVersion.current;
    setEvaluating(true);
    setError("");
    try {
      const input = frozenInput || await captureEvaluationInput();
      if (scopeRef.current !== scope || operationVersion.current !== version || input.symbol !== symbol) return;
      if (frozenInput && (!requestPreview?.previewToken || Number(requestPreview.expiresAt) <= Date.now())) {
        throw new Error("This preview has expired. Prepare a fresh request.");
      }
      const response = await evaluateChart(frozenInput ? { ...input, previewToken: requestPreview.previewToken } : input);
      if (input.provider === "CODEX" && response.id) {
        localStorage.setItem(`chartEvaluationJob:${String(input.symbol).toUpperCase()}`, response.id);
      }
      if (scopeRef.current !== scope) return;
      if (input.provider === "CODEX") {
        setActiveJob(response);
      } else {
        const saved = recordToResult(response.record) || {
          evaluation: response,
          model: response.model,
          usage: response.usage,
          symbol,
          requestedSide,
          createdAt: Date.now(),
        };
        if (operationVersion.current === version) setResult(saved);
        await loadSettings();
        window.dispatchEvent(new Event("chart_evaluation_saved"));
      }
      if (scopeRef.current === scope) setShowPreview(false);
    } catch (cause) {
      if (scopeRef.current !== scope) return;
      if (cause.status === 401) {
        setAuthenticationRequired(true);
        setError("");
        return;
      }
      setError(cause.message || "Chart evaluation failed.");
    } finally {
      if (scopeRef.current === scope) setEvaluating(false);
    }
  };

  const quickValidateEvent = async () => {
    const scope = scopeRef.current;
    const targetId = String(originatingEvent?.codexTargetId || "");
    if (!targetId.startsWith("EVENT:")) return;
    setQuickValidating(true);
    setError("");
    try {
      await processCommand({ command: `codexconf a EVALUATE v ${targetId}` });
      if (scopeRef.current !== scope) return;
      setQuickValidationRequested(true);
    } catch (cause) {
      if (scopeRef.current !== scope) return;
      setError(cause.message || "Could not start quick event validation.");
    } finally {
      if (scopeRef.current === scope) setQuickValidating(false);
    }
  };

  const compactCodex = result?.evaluation?.responseMode === "CHART_COMPACT";
  const preferredCodexPlan = selectExecutableEvaluationPlan(result);
  const multiplePlans = compactCodex || Boolean(result?.evaluation?.details);
  const levelPlan = multiplePlans ? preferredCodexPlan : result?.evaluation;
  const levels = result?.evaluation ? {
    entry: levelPlan?.entry ?? null,
    stopLoss: levelPlan?.stopLoss ?? null,
    takeProfit1: levelPlan?.takeProfit1 ?? null,
    takeProfit2: levelPlan?.takeProfit2 ?? null,
  } : null;
  const usage = result?.usage;
  const inputDiagnostics = result?.record?.inputDiagnostics || result?.evaluation?.inputDiagnostics;
  const codexProvider = providers.find((item) => item.id === "CODEX");
  const assessmentModelLabel = (savedModel) => models.find((item) => item.id === savedModel)?.label
    || codexProvider?.models?.find((item) => item.id === savedModel)?.label
    || savedModel || "Model not recorded";

  return <><Modal show={show} onHide={closePopup} centered size="lg" contentClassName="bg-dark text-light border-secondary">
    <Modal.Header closeButton closeVariant="white" className="border-secondary">
      <Modal.Title>Chart evaluation <small className="text-secondary">{symbol}</small></Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
        <ButtonGroup>
          {["CUSTOM_AI", "CODEX"].map((item) => <Button
            key={item}
            size="sm"
            variant={provider === item ? "info" : "outline-info"}
            onClick={() => setProvider(item)}
          >{item === "CUSTOM_AI" ? "Custom AI" : "Codex"}</Button>)}
        </ButtonGroup>
        <span className="text-secondary">Trade side</span>
        <ButtonGroup>
          {SIDE_OPTIONS.map((side) => <Button key={side} size="sm" variant={requestedSide === side ? "primary" : "outline-secondary"} onClick={() => setRequestedSide(side)}>{side}</Button>)}
        </ButtonGroup>
        {provider === "CUSTOM_AI" ? <>
          <Form.Select size="sm" value={model} onChange={(event) => setModel(event.target.value)} style={{ width: 190 }}>
            {models.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
          </Form.Select>
          <Button size="sm" variant="outline-info" onClick={() => setShowSettings((value) => !value)}>Settings</Button>
        </> : <>
          <Form.Select
            aria-label="Codex model"
            size="sm"
            value={codexModel}
            onChange={(event) => setCodexModel(event.target.value)}
            style={{ width: 190 }}
          >
            {(codexProvider?.models || []).map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
          </Form.Select>
          <span className="badge bg-dark border border-info text-info">
            {codexProvider?.enabled === false ? "Stopped · " : ""}low chart reasoning · {codexProvider?.timeoutSeconds || 300}s · {codexProvider?.concurrency || 2} parallel
          </span>
        </>}
      </div>

      {provider === "CUSTOM_AI" && showSettings && <div className="border border-secondary rounded p-3 mb-3 bg-black bg-opacity-25">
        <Form.Group className="mb-3">
          <Form.Label>OpenAI API key</Form.Label>
          <Form.Control type="password" value={openAiKey} onChange={(event) => setOpenAiKey(event.target.value)} placeholder={profile.keyConfigured ? "Stored securely; enter only to replace" : "sk-..."} />
          <Form.Text className="text-secondary">Stored encrypted on the Insights server and never returned to the browser.</Form.Text>
        </Form.Group>
        <Form.Check inline type="switch" id="chart-evaluation-screenshots" label="Chart screenshots" checked={Boolean(profile.inputSelection?.screenshots)} onChange={(event) => setProfile({ ...profile, inputSelection: { ...profile.inputSelection, screenshots: event.target.checked } })} />
        <Form.Check inline type="switch" id="chart-evaluation-structured" label="OHLCV context" checked={Boolean(profile.inputSelection?.structuredData)} onChange={(event) => setProfile({ ...profile, inputSelection: { ...profile.inputSelection, structuredData: event.target.checked } })} />
        <Button className="ms-3" size="sm" onClick={saveProfile}>Save settings</Button>
      </div>}

      <p className="text-secondary mb-3">Captures the current 15M, 4H, and 1D charts, compact Binance derivatives data, and up to 10 existing symbol events, then returns an advisory evaluation.</p>
      {provider === "CODEX" && activeJob?.id && <div className="border border-info rounded p-2 mb-3 small">
        Codex job <code>{activeJob.id}</code> · {activeJob.state === "LEASED" ? "running" : activeJob.state.toLowerCase()}
      </div>}
      {quickValidationRequested && <div className="border border-info rounded p-2 mb-3 small">
        Quick validation started. The result will appear on this event.
      </div>}
      {authenticationRequired && <div className="border border-secondary rounded p-3 mb-3 bg-black bg-opacity-25">
        <strong className="d-block mb-1">Session expired</strong>
        <p className="text-secondary mb-3">Sign in with Telegram to continue without leaving this chart.</p>
        <TelegramIdLogin mode="callback" onTelegramPayload={completeLogin} />
      </div>}
      {error && <div className="text-danger mb-3">{error}</div>}
      {result?.evaluation && result.symbol === symbol && <div className="border-start border-4 border-info rounded-end p-3 bg-black bg-opacity-25">
        <div className="d-flex flex-wrap align-items-center gap-2 small mb-2">
          {result.model && <span className="text-secondary">Assessed with</span>}
          <strong title={result.model || undefined}>{assessmentModelLabel(result.model)}</strong>
        </div>
        <div className="d-flex align-items-center gap-2 mb-2">
          <Badge bg={result.evaluation.verdict === "ACCEPT" ? "success" : result.evaluation.verdict === "CAUTION" ? "warning" : "danger"}>{result.evaluation.verdict}</Badge>
          <Badge bg="secondary">{multiplePlans ? preferredCodexPlan?.side || "NONE" : result.evaluation.side || "NONE"}</Badge>
          <strong>{result.evaluation.score}/100</strong>
          <span className="text-secondary">{String(multiplePlans ? preferredCodexPlan?.timing || "No actionable plan" : result.evaluation.timing || "").replaceAll("_", " ")}</span>
        </div>
        {!compactCodex && <div className="mb-3">{result.evaluation.summary}</div>}
        {!compactCodex && <div className="row g-2 small mb-3">
          {[['Entry', levels.entry], ['Stop', levels.stopLoss], ['TP1', levels.takeProfit1], ['TP2', levels.takeProfit2]].map(([label, price]) => <div className="col-6 col-md-3" key={label}><div className="text-secondary">{label}</div><strong>{price || "-"}</strong></div>)}
        </div>}
        {!compactCodex && <div className="small text-success mb-2">{(result.evaluation.strengths || []).join(" | ")}</div>}
        {!compactCodex && <div className="small text-danger mb-3">{(result.evaluation.risks || []).join(" | ")}</div>}
        {provider === "CODEX" && <CodexEvaluationReport evaluation={result.evaluation} />}
        {result.record?.eventContext?.length > 0 && <details className="small mb-3">
          <summary>{result.record.eventContext.length} Insights events used</summary>
          <ul className="mt-2 mb-0">
            {result.record.eventContext.map((event) => <li key={event.id}>{event.name} · {event.timeframe || "-"} · {event.status || "-"}</li>)}
          </ul>
        </details>}
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <Button size="sm" variant="outline-info" disabled={!levels.entry} onClick={() => onDrawLevels?.(levels)}>Draw levels</Button>
          <Button size="sm" disabled={!preferredCodexPlan} onClick={() => preferredCodexPlan && onTakeTrade?.({
            ...result,
            evaluation: {
              ...result.evaluation,
              side: preferredCodexPlan.side,
              timing: preferredCodexPlan.timing,
              entry: preferredCodexPlan.entry,
              stopLoss: preferredCodexPlan.stopLoss,
              takeProfit1: preferredCodexPlan.takeProfit1,
              takeProfit2: preferredCodexPlan.takeProfit2,
            },
          })}>Take trade</Button>
          <div className="small text-secondary ms-auto text-end">
            {inputDiagnostics && <div>
              OHLCV {inputDiagnostics.candleCounts?.["15"] || 0} / {inputDiagnostics.candleCounts?.["240"] || 0} / {inputDiagnostics.candleCounts?.["1D"] || 0}
              {` · ${inputDiagnostics.screenshots || 0} charts`}
              {` · derivatives ${inputDiagnostics.derivativesIncluded ? "included" : "unavailable"}`}
            </div>}
            {usage && <div>
              {usage.totalTokens ?? ((usage.inputTokens || 0) + (usage.outputTokens || 0))} total tokens
              {usage.inputTokens != null && usage.outputTokens != null
                ? ` · ${usage.inputTokens} in / ${usage.outputTokens} out`
                : ""}
            </div>}
          </div>
        </div>
      </div>}
      {(history?.[provider] || []).length > 1 && <div className="mt-3">
        <div className="small text-secondary mb-2">Shared {provider === "CODEX" ? "Codex" : "Custom AI"} history</div>
        <div className="d-flex flex-wrap gap-2">
          {history[provider].map((record) => <Button
            key={record.id}
            size="sm"
            variant={result?.record?.id === record.id ? "info" : "outline-secondary"}
            onClick={() => setResult(recordToResult(record))}
          >{new Date(record.completedAt).toLocaleString()} · {assessmentModelLabel(record.model)} · {record.result?.responseMode === "REFERENCE_V1"
            ? `Reference · Successful ${record.result.successScore ?? "n/a"}/100 · Failed ${record.result.failureScore ?? "n/a"}/100`
            : record.result?.score ?? "-"}</Button>)}
        </div>
      </div>}
    </Modal.Body>
    <Modal.Footer className="border-secondary">
      <Button variant="secondary" onClick={closePopup}>Close</Button>
      {String(originatingEvent?.codexTargetId || "").startsWith("EVENT:") && <Button
        variant="outline-warning"
        onClick={quickValidateEvent}
        disabled={quickValidating || quickValidationRequested}
      >{quickValidating ? <><Spinner size="sm" /> Starting…</> : "Quick validate event"}</Button>}
      <Button variant="outline-info" onClick={previewRequest} disabled={authenticationRequired || capturing || previewing || evaluating}>
        {capturing && previewing ? <><Spinner size="sm" /> Capturing current charts…</> : previewing ? <><Spinner size="sm" /> Preparing preview…</> : "Preview request"}
      </Button>
      <Button onClick={() => evaluate()} disabled={authenticationRequired || capturing || previewing || evaluating || (provider === "CUSTOM_AI" && !profile.keyConfigured) || (provider === "CODEX" && codexProvider?.enabled === false)}>{capturing && evaluating ? <><Spinner size="sm" /> Capturing current charts…</> : evaluating ? <><Spinner size="sm" /> Evaluating</> : "Evaluate current chart"}</Button>
    </Modal.Footer>
  </Modal>

  <Modal show={showPreview} onHide={() => setShowPreview(false)} centered size="xl" contentClassName="bg-dark text-light border-secondary">
    <Modal.Header closeButton closeVariant="white" className="border-secondary">
      <Modal.Title>Request preview <small className="text-secondary">{symbol}</small></Modal.Title>
    </Modal.Header>
    <Modal.Body>
      {previewExpired && <p className="text-warning">This snapshot has expired. Close the preview and prepare a fresh request.</p>}
      {requestPreview && <Tabs defaultActiveKey="summary" className="mb-3">
        <Tab eventKey="summary" title="Summary">
          <div className="row g-3 small">
            <div className="col-6 col-md-3"><div className="text-secondary">Model</div><strong>{requestPreview.model}</strong></div>
            <div className="col-6 col-md-3"><div className="text-secondary">Fingerprint</div><code>{requestPreview.fingerprint}</code></div>
            <div className="col-6 col-md-3"><div className="text-secondary">OHLCV 15M / 4H / 1D</div><strong>{requestPreview.inputDiagnostics?.candleCounts?.["15"] || 0} / {requestPreview.inputDiagnostics?.candleCounts?.["240"] || 0} / {requestPreview.inputDiagnostics?.candleCounts?.["1D"] || 0}</strong></div>
            <div className="col-6 col-md-3"><div className="text-secondary">Screenshots</div><strong>{requestPreview.screenshots?.length || 0}</strong></div>
            <div className="col-6 col-md-3"><div className="text-secondary">Derivatives</div><strong>{requestPreview.inputDiagnostics?.derivativesIncluded ? "Included" : "Unavailable"}</strong></div>
            <div className="col-6 col-md-3"><div className="text-secondary">Prompt size</div><strong>{requestPreview.inputDiagnostics?.promptCharacters || 0} characters</strong></div>
            <div className="col-12"><div className="text-secondary mb-1">Screenshot payloads</div>{requestPreview.screenshots?.map((item) => <Badge bg="secondary" className="me-2" key={item.timeframe}>{item.timeframe}: {Math.round(item.characters / 1024)} KB</Badge>)}</div>
          </div>
        </Tab>
        <Tab eventKey="prompt" title="Prompt">
          <pre className="bg-black bg-opacity-50 border border-secondary rounded p-3 small text-light" style={{ maxHeight: "55vh", overflow: "auto", whiteSpace: "pre-wrap" }}>{requestPreview.prompt}</pre>
        </Tab>
        <Tab eventKey="ohlcv" title="OHLCV">
          <pre className="bg-black bg-opacity-50 border border-secondary rounded p-3 small text-light" style={{ maxHeight: "55vh", overflow: "auto" }}>{JSON.stringify(requestPreview.context, null, 2)}</pre>
        </Tab>
        <Tab eventKey="images" title="Images">
          <div className="row g-3">{previewInput?.screenshots?.map((item) => <div className="col-12 col-lg-4" key={item.timeframe}><div className="text-secondary mb-1">{item.timeframe}</div><img src={item.dataUrl} alt={`${item.timeframe} chart capture`} className="img-fluid border border-secondary rounded" /></div>)}</div>
        </Tab>
        <Tab eventKey="raw" title="Raw request">
          <pre className="bg-black bg-opacity-50 border border-secondary rounded p-3 small text-light" style={{ maxHeight: "55vh", overflow: "auto" }}>{JSON.stringify(requestPreview.request, null, 2)}</pre>
        </Tab>
      </Tabs>}
    </Modal.Body>
    <Modal.Footer className="border-secondary">
      <Button variant="secondary" onClick={() => setShowPreview(false)}>Close</Button>
      <Button onClick={() => evaluate(previewInput)} disabled={evaluating || previewExpired || !requestPreview?.previewToken || !previewInput || (previewInput?.provider === "CUSTOM_AI" && !profile.keyConfigured)}>{evaluating ? <><Spinner size="sm" /> Evaluating</> : "Send this request"}</Button>
    </Modal.Footer>
  </Modal></>;
}

export default ChartEvaluationPopup;
